import React, { useMemo } from 'react';
import { useFarmerFormValidation } from '@applicationKYC/LegalEntityQuestionnaire/validation/useFarmerFormValidation';
import * as Yup from 'yup';
import { Trans, useTranslation } from 'react-i18next';
import breakpointSizes from '@applicationKYC/LegalEntityQuestionnaire/styled-theme-styles/global-style-variables.style';
import useWindowDimensions from '@applicationKYC/LegalEntityQuestionnaire/Utils/window-size.service';
import { Field, Loader, TextArea } from '@single-platform/components';
import {
  FarmersQuestionnaireHeader,
  PageSecondaryTextStyles
} from '@applicationKYC/FarmersQuestionnaire/farmersQuestionnaire.style';
import {
  FormStyles,
  PageHeader
} from '@applicationKYC/LegalEntityQuestionnaire/screens/renderPage/renderPage.style';
import { ButtonSection } from '@applicationKYC/LegalEntityQuestionnaire/components';
import { useSelector } from 'react-redux';
import {
  ButtonSectionWrapperStyle,
  EmphasizedBrandColorText
} from '@applicationKYC/FarmersQuestionnaire/questionnaireSections/formStyles';
import { RadioChipOption } from '@applicationKYC/FarmersQuestionnaire/components';
import { yesNoChipOptions } from '@applicationKYC/FarmersQuestionnaire/constants';
import { selectKycAppState } from '@redux';

const defaultValues = {
  doesFarmUseCrypto: null,
  proportionOfPaymentsInCrypto: null,
  howCryptoIsUsed: null
};

const cryptoAmountOptions = [
  {
    label: 'choices_proportion_of_payments_crypto_less_than_10_percent',
    value: 'LessThan10Percent'
  },
  {
    label: 'choices_proportion_of_payments_crypto_from_11_to_20_percent',
    value: 'From11PercentTo20Percent'
  },
  {
    label: 'choices_proportion_of_payments_crypto_from_21_to_30_percent',
    value: 'From21PercentTo30Percent'
  },
  {
    label: 'choices_proportion_of_payments_crypto_more_than_31_percent',
    value: 'MoreThan31Percent'
  }
];

export const FarmCryptoInformationPage = ({ onSubmit, isAtBottom }) => {
  const { t } = useTranslation();
  const { width } = useWindowDimensions();
  const { loading, farmerInformation } = useSelector((state) => state.FarmersQuestionnaireReducer);
  const isBank = process.env.REACT_APP_TRADEMARK.includes('Bank');
  const applicationState = useSelector(selectKycAppState);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        doesFarmUseCrypto: Yup.bool().required(t('validation_must_be_one_of')).nullable(),
        proportionOfPaymentsInCrypto: Yup.string().when('doesFarmUseCrypto', {
          is: true,
          then: Yup.string().required(t('validation_must_be_one_of')).nullable(),
          otherwise: Yup.string().notRequired().nullable()
        }),
        howCryptoIsUsed: Yup.string().when('doesFarmUseCrypto', {
          is: true,
          then: Yup.string().required(t('validation_required_field')).nullable(),
          otherwise: Yup.string().notRequired().nullable()
        })
      }),
    [t]
  );

  const existingValues =
    farmerInformation?.farmPaymentsAndTransactionsSection?.farmCryptoInformationPage;
  const initialValues = { ...defaultValues, ...existingValues };

  const { validationHelpers, getValues, setTouchedOnAll, handleSubmit } = useFarmerFormValidation({
    initialValues,
    validationSchema
  });

  const onHandleSubmit = async (e) => {
    e.preventDefault();
    await handleSubmit(
      async (data) => {
        if (data.doesFarmUseCrypto === false) {
          await onSubmit?.({ doesFarmUseCrypto: false }, 'farmCryptoInformationPage');
        } else {
          await onSubmit?.(data, 'farmCryptoInformationPage');
        }
      },
      () => setTouchedOnAll()
    )();
  };

  if (loading) return <Loader />;

  const values = getValues();

  return (
    <FormStyles small={width < breakpointSizes.phone}>
      <FarmersQuestionnaireHeader noSubHeader>
        <PageHeader>
          <Trans
            t={t}
            components={{ coloredText: <EmphasizedBrandColorText isBank={isBank} /> }}
            i18nKey={'farmers_crypto_header'}
          />
        </PageHeader>
      </FarmersQuestionnaireHeader>

      <Field
        Component={RadioChipOption}
        options={yesNoChipOptions}
        value={values.doesFarmUseCrypto}
        name="doesFarmUseCrypto"
        validateOnChange
        validationHelpers={validationHelpers}
        isInModal
        disabled={applicationState?.state === 'UserFillingApplication' ? false : true}
      />

      {values.doesFarmUseCrypto && (
        <>
          <PageSecondaryTextStyles>{t('farmers_crypto_percentage')}</PageSecondaryTextStyles>

          <Field
            Component={RadioChipOption}
            options={cryptoAmountOptions}
            value={values.proportionOfPaymentsInCrypto}
            name="proportionOfPaymentsInCrypto"
            validateOnChange
            validationHelpers={validationHelpers}
            isInModal
            disabled={applicationState?.state === 'UserFillingApplication' ? false : true}
          />

          <PageSecondaryTextStyles>{t('farmers_how_crypto_is_used')}</PageSecondaryTextStyles>

          <Field
            Component={TextArea}
            name="howCryptoIsUsed"
            value={values.howCryptoIsUsed}
            placeholder={t('question_enter_description')}
            validationHelpers={validationHelpers}
            required
            validateOnChange
            autoHeight={true}
            maxHeight={288}
            validateOnBlur
            maxLength={500}
            counter={true}
            disabled={applicationState?.state === 'UserFillingApplication' ? false : true}
          />
        </>
      )}

      <ButtonSectionWrapperStyle isAtBottom={isAtBottom}>
        <ButtonSection
          secondButtonClick={onHandleSubmit}
          secondButtonPrimary
          secondButtonText={
            applicationState?.state === 'UserFillingApplication'
              ? t('button_save_and_continue')
              : t('button_continue')
          }
        />
      </ButtonSectionWrapperStyle>
    </FormStyles>
  );
};
